import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import { cssGenerator } from '@/mixins/css-generator';
import { products } from '@/mixins/products';
import tooltip from '@/mixins/components/tooltip';
import { SQUASH_DROPLET } from '@/constants';
import DropletOptions from '../_bubble-options';
import ProductGridOptions from './components/_edit-options';
import schema from './droplet.schema';
const dropletSchema = schema();

export default Vue.component('product-grid', {
  template: require('./template.html'),
  name: 'product-grid',
  mixins: [cssGenerator, products],
  computed: {
    ...mapGetters([
      'currentSite',
      'currentPage',
      'viewport',
      'localStyle',
      'activeDroplet',
      'activeState',
      'animationPreview',
      'dropletObserver',
      'dragging',
      'globalBubbleOptionsOpen',
      'onPublishItem',
    ]),
    roamingTemplate() {
      return this.currentPage.type !== 'template' && this.droplet.templateRegion;
    },
    isActiveDroplet() {
      return this.activeDroplet._id === this.droplet._id;
    },
    hideTransform() {
      return this.dragging || this.globalBubbleOptionsOpen;
    },
    profile() {
      return this._dropletType(this.droplet);
    },
    storeData() {
      return this.currentSite.dynamic && this.currentSite.dynamic.products;
    },
    currencyType() {
      const enabled = this.storeData;
      const symbols = {
        USD: '&#36;',
        CAD: '&#36;',
        EUR: '&#128;',
      };
      const type = enabled && enabled.currency ? enabled.currency : 'USD';
      return {
        type: type.toLowerCase(),
        symbol: symbols[type],
      };
    },
    feed() {
      const enabled = this.storeData;
      const feed = enabled ? _.cloneDeep(enabled.published) : [];
      _.forEach(feed, (product) => {
        product.image = this._servePrefix(product.image) + product.image;
        product.templatePrice = `${this.currencyType.symbol}${product.price}`;
        product.displayPricing = this.getPrices({ product, store: this.storeData });
      });
      return feed;
    },
    displayStatus() {
      let settings = {
        desktop: 'default',
        tablet: 'default',
        mobile: 'default',
      };
      const selector = _.find(this.droplet.mediaQueries, { selector: ', .drzProduct-grid' });
      if (selector) {
        const property = _.find(selector.properties, { property: 'display' });
        if (property) {
          settings = property;
        }
      }
      return settings;
    },
  },
  data() {
    return {
      type: dropletSchema.type,
      SQUASH_DROPLET,
      display: {
        top: null,
        left: null,
        right: null,
        bottom: null,
        ctPaddingTop: null,
        ctPaddingBottom: null,
        ctPaddingLeft: null,
        ctPaddingRight: null,
        transform: null,
        perspective: null,
        searchBrdrWidthRight: null,
        searchBrdrWidthLeft: null,
        searchBrdrWidthTop: null,
        searchBrdrWidthBottom: null,
        searchBrdrRadiusTopLeft: null,
        searchBrdrRadiusTopRight: null,
        searchBrdrRadiusBottomLeft: null,
        searchBrdrRadiusBottomRight: null,
        searchLetterSpacing: null,
        barRadiusTopRight: null,
        barRadiusTopLeft: null,
        barRadiusBottomRight: null,
        barRadiusBottomLeft: null,
        fbPaddingTop: null,
        fbPaddingBottom: null,
        fbPaddingLeft: null,
        fbPaddingRight: null,
        'box-shadow': null,
        'letter-spacing': null,
        'border-top-width': null,
        'border-bottom-width': null,
        'border-left-width': null,
        'border-right-width': null,
        'border-top-right-radius': null,
        'border-top-left-radius': null,
        'border-bottom-right-radius': null,
        'border-bottom-left-radius': null,
      },
      hidden: false,
      dropletHovered: false,
      setBubbleSize: 'dropletBubble-normal',
      showEditBubble: false,
      showRevealClass: false,
    };
  },
  props: ['droplet', 'items', 'index'],
  components: {
    DropletOptions,
    ProductGridOptions,
    tooltip,
  },
  watch: {
    isActiveDroplet(is) {
      if (is) {
        this._scrollToDroplet(this.$el);
      }
    },
    'activeDroplet._id': function c(id) {
      if (!id) {
        this.showRevealClass = false;
      }
    },
    activeState(state) {
      if (state === ':reveal' && this.isActiveDroplet) {
        this.showRevealClass = true;
      } else {
        this.showRevealClass = false;
      }
    },
    animationPreview(should) {
      if (should && this.isActiveDroplet) {
        const droplet = this.$refs.dropletRow;
        const $droplet = $(droplet);
        $droplet.drzOnReveal({
          editing: this.isActiveDroplet,
          child: '.drzProduct-grid',
        });
      }
    },
    viewport() {
      this.callCssGenerator();
      this.observeVisibility();
    },
    'localStyle.value': function setLocals() {
      this.setDisplay();
    },
    dropletObserver(id) {
      if (this.droplet._id === id) {
        this.observeVisibility();
        this.$store.dispatch('setDropletObserver', false);
      }
    },
    'onPublishItem.last': function discountWatch() {
      // since this droplet's dynamic items (products) are rendered
      // using a picazzo plugin, we waant to re-render it when publishing
      // or removing items
      if (this.onPublishItem.type === 'products') {
        this.destroyPlugin();
        this.initPlugin();
      }
    },
  },
  mounted() {
    this.callCssGenerator();
    this.observeVisibility();
    this.initPlugin();
  },
  methods: {
    callCssGenerator() {
      return this.generateCSS({
        drp: this.droplet,
      });
    },
    observeVisibility() {
      const display = this.displayStatus;
      if (display[this.viewport] === 'none' || (display[this.viewport] === 'default' && display.desktop === 'none')) {
        this.hidden = true;
      } else {
        this.hidden = false;
      }
    },
    setDisplay() {
      if (this.activeDroplet._id === this.droplet._id) {
        const styles = this.localStyle;
        if (styles.properties.length > 0) {
          _.forEach(styles.properties, (property) => {
            this.$set(this.display, property, styles.value);
          });
        } else {
          _.forOwn(this.display, (value, key) => {
            this.display[key] = null;
          });
        }
      }
    },
    toggleBubble() {
      if (!this.roamingTemplate) {
        this.showEditBubble = !this.showEditBubble;
      }
    },
    destroyPlugin() {
      const $droplet = $(this.$refs.productGrid);
      $droplet.drzFilterGrid.destroy($droplet);
    },
    initPlugin() {
      const $droplet = $(this.$refs.productGrid);
      const enabled = this.storeData;
      const categories = enabled ? enabled.categories : [];
      const customCategories = this.droplet.customCategories;
      let filters = false;
      if (!this.droplet.hideFilters && !this.droplet.customList && categories.length > 0) {
        filters = categories;
      }
      if (!this.droplet.hideFilters && this.droplet.customList && customCategories.length > 0) {
        filters = customCategories;
      }
      let feed = this.feed;
      if (this.droplet.customList) {
        feed = feed.filter(p => customCategories.some(f => p.categories.indexOf(f) >= 0));
      }
      const symbol = this.currencyType.symbol;
      $droplet.drzFilterGrid({
        output(data) {
          return `
          <a class="drzProduct-grid-card" href="${data.pageLink}">
            <div class="drzProduct-grid-cardImageWrap">
              <img class="drzProduct-grid-cardImage" data-asset="${data.image}" alt="${data.name}" />
            </div>
            <div class="drzProduct-grid-cardInfo">
              <span class="drzProduct-grid-name">${data.name}</span>
              <span class="drzProduct-grid-price">
              ${data.displayPricing.original ? `<span class="drzProduct-grid-priceOld">${data.templatePrice}</span>` : ''}
              ${symbol}${data.displayPricing.live}
              <button data-product-id="${data._id}" class="drzProduct-grid-buyBtn"></button>
              </span>
            </div>
          </a>
          `;
        },
        onRender($card) {
          $card.click(e => e.preventDefault());
          const $btn = $card.find('.drzProduct-grid-buyBtn');
          $btn.click((e) => {
            e.preventDefault();
          });
        },
        filters,
        pagination: this.droplet.paginate,
        sortKey: this.droplet.sortKey,
        sort: this.droplet.sort,
        filterText: this.droplet.filterText,
        searchKeys: ['name', 'description'],
        feed,
      });
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/droplets/product-grid/index.js